import { useState, useEffect, useRef, useCallback } from "react";
import "@/App.css";
import { Toaster, toast } from "sonner";
import { ThemeProvider, useTheme } from "@/lib/ThemeContext";
import { AuthProvider, useAuth } from "@/lib/AuthContext";
import Navbar from "@/components/Navbar";
import HeroSection from "@/components/HeroSection";
import PromptSection from "@/components/PromptSection";
import DBViewer from "@/components/DBViewer";
import AuthModal from "@/components/AuthModal";
import OnboardingModal from "@/components/OnboardingModal";
import UserDashboard from "@/components/UserDashboard";
import CreditsBanner from "@/components/CreditsBanner";
import LoadingState from "@/components/LoadingState";
import { ValueSection, WhyNotChatGPTSection, UseCasesSection, PricingSection, BottomCTA, Footer } from "@/components/PageSections";
import { startGeneration, pollGeneration } from "@/lib/api";

const POLL_INTERVAL = 2000;
const MAX_POLLS = 60;

function AppContent() {
  const { theme } = useTheme();
  const { user, checkAuth, verifyToken, onboardUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [loadMsg, setLoadMsg] = useState("");
  const [db, setDb] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [modelUsed, setModelUsed] = useState("");
  const [error, setError] = useState("");
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const [dashboardOpen, setDashboardOpen] = useState(false);
  const [dashboardTab, setDashboardTab] = useState("profile");
  const [limitReached, setLimitReached] = useState(false);
  const resultRef = useRef(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    if (token && window.location.pathname === '/auth/verify') {
      verifyToken(token).then(() => {
        window.history.replaceState({}, '', '/');
        toast.success("Logged in successfully!");
      }).catch(() => {
        toast.error("Login link expired or invalid");
        window.history.replaceState({}, '', '/');
      });
    }
    if (params.get('credits_added') === 'true') {
      checkAuth();
      toast.success("Credits added to your account!");
      setDashboardOpen(true);
      setDashboardTab("billing");
      window.history.replaceState({}, '', '/');
    }
  }, []);

  const handleGenerate = useCallback(async (prompt) => {
    if (!prompt?.trim()) return;
    setLoading(true);
    setError("");
    setDb(null);
    setLimitReached(false);
    setJobId(null);
    setModelUsed("");
    setLoadMsg("Starting generation...");

    try {
      const { job_id, model_used } = await startGeneration(prompt);
      setJobId(job_id);
      setModelUsed(model_used || "");
      setLoadMsg("Generating schema and realistic data...");

      let polls = 0;
      while (polls < MAX_POLLS) {
        await new Promise((r) => setTimeout(r, POLL_INTERVAL));
        polls++;

        try {
          const result = await pollGeneration(job_id);

          if (result.status === "completed") {
            setDb(result);
            setModelUsed(result.model_used || model_used || "");
            const costDesc = result.user_charge_cents
              ? `${result.tables_count} tables, ${result.rows_count} rows — Cost: $${(result.user_charge_cents / 100).toFixed(2)}`
              : `${result.tables_count} tables, ${result.rows_count} rows`;
            toast.success("Database generated successfully", {
              description: costDesc,
            });
            setTimeout(() => {
              resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 200);
            setLoading(false);
            setLoadMsg("");
            return;
          }

          if (result.status === "failed") {
            setError(result.error || "Generation failed. Try a different prompt.");
            setLoading(false);
            setLoadMsg("");
            return;
          }

          if (polls > 5) setLoadMsg("Building relational integrity across tables...");
          if (polls > 15) setLoadMsg("Almost there — validating data consistency...");
        } catch {
          // Poll request failed — retry
        }
      }

      setError("Generation is taking longer than expected. Please try again with a simpler prompt.");
    } catch (e) {
      const status = e.response?.status;
      if (status === 402) {
        setLimitReached(true);
        setError("");
      } else {
        const detail = e.response?.data?.detail || e.message;
        setError(`Generation failed: ${detail}`);
      }
    }

    setLoading(false);
    setLoadMsg("");
  }, []);

  // Show onboarding modal when user signs in for the first time (no name set)
  useEffect(() => {
    if (user && user.needs_onboarding) {
      setOnboardingOpen(true);
    }
  }, [user]);

  const handleOnboardingComplete = async (profile) => {
    await onboardUser(profile);
    setOnboardingOpen(false);
    toast.success("Profile saved!", { description: "Welcome to MockDB." });
  };

  const openSignIn = () => setAuthModalOpen(true);

  const handleReopenJob = useCallback((jobResult) => {
    setDb(jobResult);
    setJobId(jobResult.job_id);
    setModelUsed(jobResult.model_used || "");
    setDashboardOpen(false);
    setTimeout(() => {
      resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 300);
  }, []);

  return (
    <div className="min-h-screen" style={{ background: "var(--sdb-bg)", color: "var(--sdb-text)" }}>
      <Toaster
        theme={theme}
        position="top-center"
        toastOptions={{
          style: {
            background: "var(--sdb-surface)",
            border: "1px solid var(--sdb-border)",
            color: "var(--sdb-text)",
            fontFamily: "'Inter', sans-serif",
          },
        }}
      />

      <AuthModal open={authModalOpen} onOpenChange={setAuthModalOpen} />
      <OnboardingModal
        open={onboardingOpen}
        onOpenChange={setOnboardingOpen}
        onComplete={handleOnboardingComplete}
      />
      <UserDashboard
        open={dashboardOpen}
        onOpenChange={setDashboardOpen}
        defaultTab={dashboardTab}
        onReopenJob={handleReopenJob}
        currentDb={db}
      />

      <Navbar
        onOpenAuth={openSignIn}
        onOpenDashboard={() => { setDashboardTab("profile"); setDashboardOpen(true); }}
      />
      <HeroSection />
      <PromptSection onGenerate={handleGenerate} loading={loading} error={error} />

      <section
        ref={resultRef}
        data-testid="results-section"
        className="max-w-[960px] mx-auto px-6 pb-16"
      >
        {loading && <LoadingState message={loadMsg} />}
        {limitReached && <CreditsBanner onSignIn={() => setAuthModalOpen(true)} />}
        {db && <DBViewer data={db.data || db} jobId={jobId} modelUsed={modelUsed} onSignUp={openSignIn} expiresAt={db.expires_at} />}
      </section>

      <ValueSection />
      <WhyNotChatGPTSection />
      <UseCasesSection />
      <PricingSection onSignUp={openSignIn} />
      <BottomCTA onSignUp={openSignIn} />
      <Footer />
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
